
import GoalSurveyStatisticsResponse from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/StatisticsService/GoalSurveyStatisticsResponse";
import TopCategoryItem from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/StatisticsService/TopCategoryItem";
import Vue from "vue";
import { Component } from "vue-property-decorator";

interface SurveyDisplayItem {
  key: string;
  label: string;
  count: number;
  percentage: number;
  type: number | string | null;
}

interface SurveyDisplayGroup {
  key: string;
  title: string;
  items: SurveyDisplayItem[];
}

interface TopCategoryDisplayItem {
  key: string;
  name: string;
  value: string;
}

@Component({
  name: "goal-survey-statistics",
})
export default class GoalSurveyStatisticsComponent extends Vue {
  isLoading = true;
  loadError = "";
  totalSurveys = 0;
  surveyGroups: SurveyDisplayGroup[] = [];
  topCategories: TopCategoryDisplayItem[] = [];

  async created() {
    await this.loadStatistics();
  }

  async loadStatistics() {
    this.isLoading = true;
    this.loadError = "";

    await Promise.all([
      this.loadSurveyStatistics(),
      this.loadTopCategories(),
    ]);

    this.isLoading = false;
  }

  async loadSurveyStatistics() {
    try {
      const response = await this.$api.StatisticsService.getGoalSurveyStatisticsAsync();
      this.totalSurveys = this.readNumber(response, "TotalSurveys", "totalSurveys");
      this.surveyGroups = [
        this.buildGroup("goals", "Цель использования приложения", this.readItems(response, "Goals", "goals"), this.goalLabel),
        this.buildGroup("experiences", "Опыт практик", this.readItems(response, "Experiences", "experiences"), this.experienceLabel),
        this.buildGroup("skills", "Навыки и направления", this.readItems(response, "Skills", "skills"), this.skillLabel),
        this.buildGroup("dayTimes", "Удобное время практики", this.readItems(response, "DayTimes", "dayTimes"), this.dayTimeLabel),
        this.buildGroup("genders", "Пол", this.readItems(response, "Genders", "genders"), this.genderLabel),
      ];
    } catch (error) {
      this.appendLoadError("Статистика анкеты недоступна");
      this.surveyGroups = [
        this.buildGroup("goals", "Цель использования приложения", [], this.goalLabel),
        this.buildGroup("experiences", "Опыт практик", [], this.experienceLabel),
        this.buildGroup("skills", "Навыки и направления", [], this.skillLabel),
        this.buildGroup("dayTimes", "Удобное время практики", [], this.dayTimeLabel),
        this.buildGroup("genders", "Пол", [], this.genderLabel),
      ];
    }
  }

  async loadTopCategories() {
    const dateEnd = new Date();
    const dateStart = this.addDays(dateEnd, -30);

    try {
      const response = await this.$api.StatisticsService.getTopAffirmationsAsync({
        DateStart: dateStart,
        DateEnd: dateEnd,
      });
      const items = (response && (response.Items || (response as any).items)) || [];
      this.topCategories = items.slice(0, 10).map((item: TopCategoryItem, index: number) => ({
        key: String(item.Id || (item as any).id || index),
        name: item.Name || (item as any).name || `Категория #${index + 1}`,
        value: this.formatNumber(item.ListenCount || (item as any).listenCount || 0),
      }));
    } catch (error) {
      this.appendLoadError("Топ категорий недоступен");
      this.topCategories = [];
    }
  }

  buildGroup(
    key: string,
    title: string,
    items: GoalSurveyStatisticsResponse[],
    labelResolver: (type: number | string | null) => string
  ): SurveyDisplayGroup {
    return {
      key,
      title,
      items: items.map((item, index) => {
        const type = this.readType(item);
        return {
          key: `${key}-${type === null ? "empty" : type}-${index}`,
          type,
          label: labelResolver(type),
          count: this.readNumber(item, "Count", "count"),
          percentage: this.readNumber(item, "Percentage", "percentage"),
        };
      }),
    };
  }

  readItems(response: any, pascalKey: string, camelKey: string): GoalSurveyStatisticsResponse[] {
    const items = response && (response[pascalKey] || response[camelKey]);
    return Array.isArray(items) ? items : [];
  }

  readNumber(source: any, pascalKey: string, camelKey: string): number {
    if (!source) {
      return 0;
    }
    const value = source[pascalKey] != null ? source[pascalKey] : source[camelKey];
    return Number(value || 0);
  }

  readType(source: any): number | string | null {
    if (!source) {
      return null;
    }
    const value = source.Type !== undefined ? source.Type : source.type;
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? String(value) : numericValue;
  }

  goalLabel(type: number | string | null): string {
    switch (type) {
      case null: return "Не указано";
      case 0: return "Не указано";
      case 1:
      case "SelfEsteem": return "Самооценка и уверенность";
      case 2:
      case "Health": return "Здоровье и благополучие";
      case 4:
      case "Success": return "Успех и достижение целей";
      case 8:
      case "Love": return "Любовь и отношения";
      case 16:
      case "Finance": return "Финансовое благополучие";
      default: return `Вариант ${type}`;
    }
  }

  experienceLabel(type: number | string | null): string {
    switch (type) {
      case null: return "Не указан";
      case 0:
      case "Beginner": return "Начинающий";
      case 1:
      case "Intermediate": return "Средний";
      case 2:
      case "Advanced": return "Опытный";
      default: return `Вариант ${type}`;
    }
  }

  skillLabel(type: number | string | null): string {
    switch (type) {
      case null: return "Не указано";
      case 0: return "Не указано";
      case 1:
      case "SelfEsteem": return "Самооценка и уверенность";
      case 2:
      case "Health": return "Здоровье и благополучие";
      case 4:
      case "Success": return "Успех и достижение целей";
      case 8:
      case "Love": return "Любовь и отношения";
      case 16:
      case "Finance": return "Финансовое благополучие";
      case 32:
      case "PeaceAndStress": return "Спокойствие и стресс-менеджмент";
      default: return `Вариант ${type}`;
    }
  }

  dayTimeLabel(type: number | string | null): string {
    switch (type) {
      case null: return "Не указано";
      case 0:
      case "Morning": return "Утро";
      case 1:
      case "Day": return "День";
      case 2:
      case "Evening": return "Вечер";
      case 3:
      case "Night": return "Ночь";
      default: return `Вариант ${type}`;
    }
  }

  genderLabel(type: number | string | null): string {
    switch (type) {
      case null: return "Не указан";
      case 0:
      case "Male": return "Мужской";
      case 1:
      case "Female": return "Женский";
      default: return `Вариант ${type}`;
    }
  }

  getBarWidth(value: number): string {
    return `${Math.max(0, Math.min(100, value || 0))}%`;
  }

  formatPercent(value: number): string {
    return `${Number(value || 0).toFixed(2)}%`;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat("ru-RU").format(value || 0);
  }

  addDays(date: Date, days: number): Date {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
  }

  appendLoadError(message: string) {
    this.loadError = this.loadError ? `${this.loadError}; ${message}` : message;
  }
}
